/**
 * Information-gain assets for Batches 2 & 3 (Hiring Strategy + Recruitment).
 *
 * Each article already carries a strong answer-first lead, a single homepage
 * contextual link, and a migrated `/book` CTA in source. The only remaining
 * gap (per CONTENT_OPTIMIZATION_GUIDE §11) is the labeled, citable
 * information-gain asset. Each asset heading is a unique sentinel string so
 * injection is idempotent.
 */
export const BATCH23_INFO_GAIN: Record<string, string> = {
  // ===== Batch 2 — Hiring Strategy / platform cluster =====
  "best-platform-hiring-specialized-automation-vas": `
<h2>The Sourcing Channel Decision Matrix</h2>
<p>No channel is best for every role. Match the channel to how specialized the hire is — the signal quality drops as you move down:</p>
<table class="w-full text-left border-collapse my-6">
  <thead>
    <tr class="border-b border-border">
      <th class="py-2 pr-4 font-semibold">Role complexity</th>
      <th class="py-2 pr-4 font-semibold">Best channel</th>
      <th class="py-2 font-semibold">Signal quality</th>
    </tr>
  </thead>
  <tbody class="text-sm">
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Generalist admin</td><td class="py-2 pr-4">OnlineJobs.ph, Upwork</td><td class="py-2">Volume (low specialization)</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Mid-specialization</td><td class="py-2 pr-4">Curated technical communities</td><td class="py-2">Moderate, thin supply</td></tr>
    <tr><td class="py-2 pr-4">Specialized architecture</td><td class="py-2 pr-4">Independent portfolios &amp; referrals</td><td class="py-2">Highest (live systems)</td></tr>
  </tbody>
</table>
<p>Defaulting every hire to the bottom row of a job board is how agencies miss the channel one talent actually lives in.</p>`,
  "hire-direct-remote-contractor-vs-outsourcing-agency": `
<h2>The One-Question Decision Rule</h2>
<p>You can collapse the whole direct-vs-agency debate into a single question:</p>
<blockquote class="border-l-2 border-primary/40 pl-4 my-4 italic"><strong>Does this role require a named specialist, or a filled seat?</strong></blockquote>
<ul>
  <li><strong>Named specialist</strong> (CRM architecture, automation engineering) → hire direct. An agency cannot guarantee which person you get, and you pay markup for that uncertainty.</li>
  <li><strong>Filled seat</strong> (high-volume admin, support) → agency is defensible. Speed and replacement matter more than which individual staffs the role.</li>
</ul>
<p>If the answer is "named specialist," the agency premium buys you less of exactly what you need.</p>`,
  "find-trusted-virtual-assistant-outside-upwork": `
<h2>The 3 Trust Signals Scorecard</h2>
<p>Outside a marketplace, trust is not a star rating — it is evidence. Score a candidate on three signals before you hire:</p>
<ul>
  <li><strong>Live system (2 pts)</strong> — can they walk you through a running build, not a screenshot?</li>
  <li><strong>Trade-off reasoning (2 pts)</strong> — when asked about a webhook failure, do they explain the decision or just the steps?</li>
  <li><strong>Mistake ownership (1 pt)</strong> — can they describe a build that broke and how they fixed it?</li>
</ul>
<p>A score of 4–5 means evidence-based trust. A candidate who claims a flawless record and shows only screenshots scores zero — no matter how polished the profile.</p>`,
  "onlinejobs-ph-review-technical-hiring": `
<h2>The Platform-Role Fit Test</h2>
<p>Before you post on OnlineJobs.ph, run the role through three questions:</p>
<ol>
  <li><strong>Is the work repeatable and well-defined?</strong> If yes, the platform's volume is an asset.</li>
  <li><strong>Does the role need architecture depth, or task execution?</strong> Architecture depth rarely lists here.</li>
  <li><strong>Can you verify the candidate on live systems?</strong> If you must rely on profiles alone, the platform cannot help you.</li>
</ol>
<p>Two or three "yes" answers favor the platform. Two or more "no" answers mean you are sourcing in the wrong channel for the role — treat it as supplementary at most.</p>`,
  "best-agencies-hire-filipino-virtual-assistants": `
<h2>What the Agency Markup Actually Buys</h2>
<p>An agency's 30–60% markup is not a single charge — it is a bundle. Knowing what you are paying for tells you whether it is worth it for your role:</p>
<table class="w-full text-left border-collapse my-6">
  <thead>
    <tr class="border-b border-border">
      <th class="py-2 pr-4 font-semibold">What the markup funds</th>
      <th class="py-2 font-semibold">Worth it for…</th>
    </tr>
  </thead>
  <tbody class="text-sm">
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Sourcing &amp; replacement</td><td class="py-2">High-volume, low-complexity seats</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Account management layer</td><td class="py-2">Roles where you don't want to manage the worker</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Compliance &amp; payroll</td><td class="py-2">Many simultaneous hires</td></tr>
    <tr><td class="py-2 pr-4">Engineering depth</td><td class="py-2">Rarely — the specialist is usually assigned, not selected</td></tr>
  </tbody>
</table>
<p>For a specialized architect, the markup funds everything except the one thing the role actually needs.</p>`,
  "upwork-alternative-high-level-tech-virtual-assistants": `
<h2>The Selection-Pressure Test</h2>
<p>Every hiring channel rewards something. Run the channel through this test to predict whether it surfaces architects or filters them out:</p>
<ul>
  <li><strong>Does the platform rank by rate competition?</strong> Yes → it selects against specialized talent.</li>
  <li><strong>Does it reward transaction volume and ratings?</strong> Yes → it favors task completion, not architecture.</li>
  <li><strong>Can a specialist show live work instead of a profile?</strong> No → the channel cannot verify the skill you need.</li>
</ul>
<p>Switching from one marketplace to another changes the logo, not the selection pressure. The alternatives that work are the ones that reward demonstrated work — portfolios, communities, and referrals.</p>`,
  "how-to-transition-an-assistant-from-task-taker-to-project-manager": `
<h2>Task-Taker vs. Manager: A Behavior Matrix</h2>
<p>You cannot promote someone and keep managing them the same way. The behavior you observe tells you whether the transition is real:</p>
<table class="w-full text-left border-collapse my-6">
  <thead>
    <tr class="border-b border-border">
      <th class="py-2 pr-4 font-semibold">Behavior</th>
      <th class="py-2 pr-4 font-semibold">Task-taker</th>
      <th class="py-2 font-semibold">Manager / architect</th>
    </tr>
  </thead>
  <tbody class="text-sm">
    <tr class="border-b border-border/50"><td class="py-2 pr-4">What they ask for</td><td class="py-2 pr-4">The next step</td><td class="py-2">The outcome and constraints</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">How they report</td><td class="py-2 pr-4">Activity completed</td><td class="py-2">Result against the metric</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">What they escalate</td><td class="py-2 pr-4">"What should I do?"</td><td class="py-2">"Here is the gap and the fix"</td></tr>
    <tr><td class="py-2 pr-4">When something breaks</td><td class="py-2 pr-4">Waits for instruction</td><td class="py-2">Flags the system problem first</td></tr>
  </tbody>
</table>
<p>If every row still reads "task-taker," you have renamed the role, not promoted the person.</p>`,
  "how-to-scaling-your-business-operations-using-remote-talent-leverage": `
<h2>Linear vs. Systems Leverage</h2>
<p>The two scaling models compound very differently. The math is what makes an architect cheaper than a team at scale:</p>
<table class="w-full text-left border-collapse my-6">
  <thead>
    <tr class="border-b border-border">
      <th class="py-2 pr-4 font-semibold">Leverage type</th>
      <th class="py-2 pr-4 font-semibold">How output grows</th>
      <th class="py-2 font-semibold">How cost grows</th>
    </tr>
  </thead>
  <tbody class="text-sm">
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Headcount (manual)</td><td class="py-2 pr-4">Linear — 2× people ≈ 2× output</td><td class="py-2">Linear — 2× salaries</td></tr>
    <tr><td class="py-2 pr-4">Systems (automated)</td><td class="py-2 pr-4">Non-linear — 1 pipeline serves 10× volume</td><td class="py-2">Flat — usage does not scale cost</td></tr>
  </tbody>
</table>
<p>Hire an architect first so the systems exist; every later hire then produces more against an instrumented foundation instead of adding manual overhead.</p>`,
  "tips-for-seamless-communication-with-filipino-virtual-assistants": `
<h2>The Ambiguity-to-Spec Conversion Checklist</h2>
<p>Most "communication problems" are ambiguity problems. Before you send an instruction, convert it from vague to executable:</p>
<ul>
  <li><strong>State the data</strong> — the exact schema, endpoint, or input the work depends on.</li>
  <li><strong>State the expected behavior</strong> — what success looks like, in measurable terms.</li>
  <li><strong>State the failure path</strong> — what happens on error, how many retries, who is alerted.</li>
  <li><strong>State the source of truth</strong> — the wiki or doc where this decision lives after the call.</li>
</ul>
<p>If you cannot fill all four, the instruction is not clear enough to delegate across a time zone — it is still a guess.</p>`,
  "how-to-build-an-internal-wiki-or-sop-library-for-your-team": `
<h2>The SOP Anti-Rot Checklist</h2>
<p>Most wikis die because they document everything once and never get touched again. A library stays alive when it passes these five checks:</p>
<ul>
  <li><strong>Failure-point first</strong> — does it start with the workflows that break most often?</li>
  <li><strong>Decision logic, not just steps</strong> — does it explain the "why" and the trade-off?</li>
  <li><strong>Single source of truth</strong> — do the systems link to it, or is it a separate folder?</li>
  <li><strong>Maintained by users</strong> — do the people who use it update it?</li>
  <li><strong>Linked from the work</strong> — does each automation point to its own doc?</li>
</ul>
<p>If a wiki fails checks 3 and 5, it is competing with Slack and memory — and it will lose.</p>`,
  "setting-kpis-and-performance-metrics-for-a-virtual-assistant": `
<h2>The KPI Ownership Test</h2>
<p>A metric is only fair if the VA can actually influence it. Before you set any KPI, run it through three questions:</p>
<ol>
  <li><strong>Authority</strong> — does the VA have the power to move this number?</li>
  <li><strong>Systems</strong> — are the tools instrumented so the metric updates without manual reporting?</li>
  <li><strong>Failure path</strong> — if the number drops, is it the person or the system that broke?</li>
</ol>
<p>If the answer to #1 is no, you are measuring the system, not the VA. If the answer to #2 is no, the KPI becomes a reporting chore that invites spin. A KPI that fails this test measures effort, not ownership.</p>`,
  "how-to-prevent-communication-breakdown-with-offshore-team-members": `
<h2>The Async Handoff Checklist</h2>
<p>When a handoff crosses a time zone, it must survive the twelve-hour gap with no follow-up. Every handoff should contain:</p>
<ul>
  <li><strong>The decision</strong> — what was decided, written, not verbal.</li>
  <li><strong>The spec</strong> — the data, endpoint, and expected behavior.</li>
  <li><strong>The status</strong> — visible in a dashboard, not asked for in chat.</li>
  <li><strong>The source-of-truth link</strong> — where the durable record of this lives.</li>
</ul>
<p>A handoff with all four executes while you sleep. A handoff missing any one becomes a lost day by morning.</p>`,

  // ===== Batch 3 — Recruitment cluster =====
  "how-to-avoid-recruitment-fees-when-hiring-from-the-philippines": `
<h2>The Fee-Type Bypass Map</h2>
<p>Recruitment fees are a function of the channel. Knowing which fee attaches where tells you which one you can eliminate by sourcing directly:</p>
<table class="w-full text-left border-collapse my-6">
  <thead>
    <tr class="border-b border-border">
      <th class="py-2 pr-4 font-semibold">Fee type</th>
      <th class="py-2 pr-4 font-semibold">Who charges it</th>
      <th class="py-2 font-semibold">How to bypass</th>
    </tr>
  </thead>
  <tbody class="text-sm">
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Placement fee</td><td class="py-2 pr-4">Agencies (% of annual)</td><td class="py-2">Source from portfolios</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Ongoing markup</td><td class="py-2 pr-4">Agencies (hourly spread)</td><td class="py-2">Contract the specialist direct</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Subscription / connect fees</td><td class="py-2 pr-4">Some marketplaces</td><td class="py-2">Use direct channels &amp; referrals</td></tr>
    <tr><td class="py-2 pr-4">None</td><td class="py-2 pr-4">Direct sourcing</td><td class="py-2">Pay the specialist only</td></tr>
  </tbody>
</table>
<p>The agency's vetting is what you pay for — and you can replicate it with evidence-based screening at no fee.</p>`,
  "outsourcing-to-the-philippines-pros-and-cons-for-small-business": `
<h2>The Structured-Role Test</h2>
<p>Outsourcing fails on ambiguity, not geography. Predict whether the Philippines will work for a given role by testing it against three conditions:</p>
<ol>
  <li><strong>Defined</strong> — can you write the outcome as a spec, or is it still "figure it out"?</li>
  <li><strong>Instrumented</strong> — is the work visible in a system, or does it depend on you watching?</li>
  <li><strong>Outcome-based</strong> — can you measure results instead of hours?</li>
</ol>
<p>Three yeses → outsourcing is low-risk; build the architecture and hire. Any "no" → fix the structure first, because offshore or onshore will both struggle with an ambiguous role.</p>`,
  "how-to-recruit-direct-premium-filipino-freelancers": `
<h2>The Premium Outreach Message Template</h2>
<p>Premium freelancers ignore generic outreach and respond to technical specificity. Lead with the problem, not the role:</p>
<ul>
  <li><strong>The technical problem</strong> — name the architecture you need (e.g., fault-tolerant lead-to-cash across multi-account snapshots).</li>
  <li><strong>The stack</strong> — the exact tools (CRM, automation platform, integrations).</li>
  <li><strong>The outcome</strong> — the result you are hiring for, stated as a system, not a task list.</li>
</ul>
<p>A message built this way gets a response because it proves you understand the work. "We need a VA, $5/hr" gets ignored because it proves you do not.</p>`,
  "is-virtualstaff-ph-safe-for-hiring-technical-vas": `
<h2>Platform Safety vs. Talent Specialization</h2>
<p>"Safe" and "specialized" are different axes. A platform can be one without the other — place any channel on this 2×2 before you trust it for a technical role:</p>
<table class="w-full text-left border-collapse my-6">
  <thead>
    <tr class="border-b border-border">
      <th class="py-2 pr-4 font-semibold"></th>
      <th class="py-2 pr-4 font-semibold">Generalist</th>
      <th class="py-2 font-semibold">Specialized</th>
    </tr>
  </thead>
  <tbody class="text-sm">
    <tr class="border-b border-border/50"><td class="py-2 pr-4 font-semibold">Safe</td><td class="py-2 pr-4">Compliance-handled admin staffing</td><td class="py-2">Rare — direct sourcing from portfolios</td></tr>
    <tr><td class="py-2 pr-4 font-semibold">Risky</td><td class="py-2 pr-4">Avoid regardless</td><td class="py-2">Avoid regardless</td></tr>
  </tbody>
</table>
<p>A platform being safe does not close the specialization gap. For architecture work, "safe + generalist" still leaves you without the specialist you actually need.</p>`,
  "how-to-find-independent-remote-contractors-in-the-philippines": `
<h2>The Found-to-Hired Conversion Funnel</h2>
<p>Finding a contractor and hiring one are different stages. Each stage has its own evidence requirement — skip one and a promising lead stalls:</p>
<ol>
  <li><strong>Source</strong> — find them where the work is visible (portfolios, stack communities, referrals).</li>
  <li><strong>Verify</strong> — ask for a live system and architecture reasoning, not a profile.</li>
  <li><strong>Trial</strong> — run a scoped, paid piece of work to test delivery rhythm.</li>
  <li><strong>Contract</strong> — lock scope, IP, payment, and termination in writing.</li>
</ol>
<p>The funnel is evidence-gated: each stage de-risks the next, so by the contract stage you are confirming fit, not discovering capability.</p>`,
  "best-virtual-assistant-companies-in-the-philippines-reviews": `
<h2>The Review-Score Reality Check</h2>
<p>A review score tells you a company delivers what it sells — not that it sells what you need. Before you trust a rating for a technical role, ask:</p>
<ul>
  <li><strong>Who wrote the reviews?</strong> If most reviewers hired generalists, the score measures generalist satisfaction.</li>
  <li><strong>What does the volume reward?</strong> High review counts come from high seat volume, which selects against specialization.</li>
  <li><strong>Does the score test architecture?</strong> If reviews measure on-time admin delivery, they say nothing about CRM engineering.</li>
</ul>
<p>If the answer to #3 is no, the review score is irrelevant to your role — evaluate the company on live systems instead.</p>`,
  "how-to-hire-a-virtual-assistant-in-the-philippines-without-an-agency": `
<h2>Where the Agency Markup Reallocates</h2>
<p>Going direct does not make the markup vanish — it lets you decide where it goes. The same dollars an agency keeps can flow three ways instead:</p>
<table class="w-full text-left border-collapse my-6">
  <thead>
    <tr class="border-b border-border">
      <th class="py-2 pr-4 font-semibold">Markup goes to…</th>
      <th class="py-2 font-semibold">What you get</th>
    </tr>
  </thead>
  <tbody class="text-sm">
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Your pocket (savings)</td><td class="py-2">Lower all-in cost</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">The specialist (higher pay)</td><td class="py-2">Better retention of top talent</td></tr>
    <tr><td class="py-2 pr-4">The system (instrumentation)</td><td class="py-2">Outcomes that don't depend on the person</td></tr>
  </tbody>
</table>
<p>The agency's value is sourcing and vetting — both of which you can replicate from portfolios. The difference is who keeps the margin.</p>`,
  "onlinejobs-ph-vs-upwork-for-hiring-filipino-talent": `
<h2>The Skip-Both Decision Rule</h2>
<p>Comparing the two platforms is only useful if your role actually belongs on a marketplace. Run the one question that tells you whether to choose or skip both:</p>
<blockquote class="border-l-2 border-primary/40 pl-4 my-4 italic"><strong>Is the role specialized technical architecture, or task-based execution?</strong></blockquote>
<ul>
  <li><strong>Task-based execution</strong> → choose: OnlineJobs.ph for longer-term direct hires, Upwork for short, scoped projects.</li>
  <li><strong>Specialized architecture</strong> → skip both. Engineers who build production systems do not compete on rate marketplaces, so no marketplace ranks them. Source directly from portfolios.</li>
</ul>
<p>If the answer is "architecture," the right move is not a better marketplace — it is no marketplace.</p>`,
  "how-to-hire-independent-remote-contractor-safely": `
<h2>The 4-Safeguard Risk Map</h2>
<p>Each safeguard mitigates a specific failure mode. Knowing which risk each one closes tells you which you can never skip:</p>
<table class="w-full text-left border-collapse my-6">
  <thead>
    <tr class="border-b border-border">
      <th class="py-2 pr-4 font-semibold">Safeguard</th>
      <th class="py-2 font-semibold">Risk it closes</th>
    </tr>
  </thead>
  <tbody class="text-sm">
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Written agreement</td><td class="py-2">Scope creep &amp; misclassification</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Verify identity &amp; prior work</td><td class="py-2">Fraud &amp; overstated capability</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">Scoped paid trial</td><td class="py-2">Bad fit locked into a long term</td></tr>
    <tr><td class="py-2 pr-4">Traceable payment</td><td class="py-2">Payment dispute &amp; no audit trail</td></tr>
  </tbody>
</table>
<p>A problem with all four in place is a contract dispute you can resolve. A problem without them is a loss you cannot recover.</p>`,
  "where-to-find-premium-operations-virtual-assistants": `
<h2>The Premium-Tier Signal Test</h2>
<p>You recognize a premium operations VA by the language they use, not the rate they quote. Listen for three markers:</p>
<ul>
  <li><strong>Outcomes, not tasks</strong> — do they describe the result the system produces, or the checklist they follow?</li>
  <li><strong>Systems, not steps</strong> — do they talk about pipelines and instrumentation, or manual processes?</li>
  <li><strong>Trade-offs, not hours</strong> — do they explain what fails and why, or just what they will do?</li>
</ul>
<p>A specialist who passes all three builds the operation. A generalist who fails them executes someone else's operation. The difference is whether they engineer the system or just run it.</p>`,
  "top-remote-hiring-platforms-b2b-business-owners": `
<h2>The Specialization Spectrum</h2>
<p>Platform lists mislead because they rank channels without the signal-quality dimension. Place any channel on the spectrum to see what it can actually surface:</p>
<table class="w-full text-left border-collapse my-6">
  <thead>
    <tr class="border-b border-border">
      <th class="py-2 pr-4 font-semibold">Level</th>
      <th class="py-2 pr-4 font-semibold">Channel</th>
      <th class="py-2 font-semibold">Signal quality</th>
    </tr>
  </thead>
  <tbody class="text-sm">
    <tr class="border-b border-border/50"><td class="py-2 pr-4">1 — Generalist</td><td class="py-2 pr-4">OnlineJobs.ph, Upwork</td><td class="py-2">Volume, low specialization</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">2 — Short-term scoped</td><td class="py-2 pr-4">Upwork (project)</td><td class="py-2">Built for time-boxed work</td></tr>
    <tr class="border-b border-border/50"><td class="py-2 pr-4">3 — Long-term generalist</td><td class="py-2 pr-4">OnlineJobs.ph (direct hire)</td><td class="py-2">Direct relationship, generalist pool</td></tr>
    <tr><td class="py-2 pr-4">4 — Specialized</td><td class="py-2 pr-4">Portfolios &amp; referrals</td><td class="py-2">Live systems, highest signal</td></tr>
  </tbody>
</table>
<p>For specialized roles, the "platforms" at level 4 are not platforms at all — they are where the work is published. Treat any ranked list as supplementary and source directly.</p>`,
  "verify-portfolio-credentials-offshore-freelancers": `
<h2>The Evidence Hierarchy for Verification</h2>
<p>Not all proof is equal. Rank what a freelancer shows you by how hard it is to fake — and weight the top of the list heaviest:</p>
<ol>
  <li><strong>Live system they can walk through in real time</strong> — cannot be staged or stolen.</li>
  <li><strong>Verifiable client corroboration</strong> — a public case study, testimonial, or live site.</li>
  <li><strong>Reasoned answer to a failure scenario</strong> — explains trade-offs, not the happy path.</li>
  <li><strong>Scoped paid trial</strong> — tests delivery rhythm, not just claimed skill.</li>
  <li><strong>Screenshot</strong> — weak; can be borrowed or fabricated.</li>
  <li><strong>Unverified claim</strong> — no weight on its own.</li>
</ol>
<p>A candidate who can only produce level 5 and 6 evidence is unverified, regardless of how polished the profile looks. Verification is about evidence, not credentials.</p>`,
};
